
import { styled } from 'stitches.config';
import { useIntl } from 'react-intl';
import Typography from '@/components/sushi/Typography';
import { toCurrency } from '@/utils/numberFormatter';
import { MonthlyPaymentGraphData } from './MonthlyPaymentsGraph';
import { palette } from 'theme/palette';
import { capitalize } from '@/utils/stringHelpers';

type MonthlyPaymentsGraphTooltipProps = {
  data: MonthlyPaymentGraphData;
  monthlyPayments: number;
}
const currentYear = new Date().getFullYear();

const januaryDate = new Date(2024, 0);

export const MonthlyPaymentsGraphTooltip = ({
  data,
  monthlyPayments,
}: MonthlyPaymentsGraphTooltipProps) => {
  const {capital, insurance, interests, year} = data;
  const { formatMessage, formatDate } = useIntl();

  return (
    <TooltipContainer>
      <Header>
        <Typography variant="bodyXS" color="secondary">
          {capitalize(formatDate(januaryDate, {
              month: 'short',
          }))} {currentYear + year - 1}
        </Typography>
        <Typography variant="bodyXS" color="secondary">
          {toCurrency(monthlyPayments, 'EUR')}
        </Typography>
      </Header>
      <TooltipInfos>
        <InfoLine>
          <Typography variant="bodyXS">
            {formatMessage({
              id: 'monthly_payments_simulator.chart.legend.insurance',
            })}
          </Typography>
          <TooltipInsurance variant="bodyXS">
            {toCurrency(insurance, 'EUR')}
          </TooltipInsurance>
        </InfoLine>
        <InfoLine>
          <Typography variant="bodyXS">
            {formatMessage({
              id: 'monthly_payments_simulator.chart.legend.interests',
            })}
          </Typography>
          <TooltipInterest variant="bodyXS">
            {toCurrency(interests, 'EUR')}
          </TooltipInterest>
        </InfoLine>
        <InfoLine>
          <Typography variant="bodyXS">
            {formatMessage({
              id: 'monthly_payments_simulator.chart.legend.capital',
            })}
          </Typography>
          <TooltipCapital variant="bodyXS">{toCurrency(capital, 'EUR')}</TooltipCapital>
        </InfoLine>
      </TooltipInfos>
    </TooltipContainer>
  );
};

const TooltipContainer = styled('div', {
  minWidth: '200px',
  backdropFilter: 'blur(12px)',
  background: 'rgba(19, 19, 20, 0.2)',
  borderRadius: '16px',
  '&:active, &:focus': {
    border: '1px solid $borderSecondary',
  },
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '12px',
  borderBottom: '1px solid $webappTertiary',
});

const TooltipInfos = styled('div', {
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const InfoLine = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
});

const TooltipInterest = styled(Typography, {
  color: `${palette.sky400} !important`,
});

const TooltipCapital = styled(Typography, {
  color: '#E54948 !important',
});

const TooltipInsurance = styled(Typography, {
  color: `${palette.brand500} !important`,
});
